"use client";

import { Tabs, TabsList, TabsTrigger } from "@pex/ui-web";
import { MapIcon, MoonIcon, SunIcon, TreesIcon } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useMap } from "../context";

interface StyleOption {
  icon: React.ReactNode;
  id: string;
  isDark?: boolean;
  label: string;
}

const STYLE_OPTIONS: StyleOption[] = [
  {
    id: "streets-v12",
    label: "Map",
    icon: <MapIcon className="h-5 w-5" />,
  },
  {
    id: "satellite-streets-v12",
    label: "Satellite",
    icon: <MapIcon className="h-5 w-5" />,
  },
  {
    id: "outdoors-v12",
    label: "Terrain",
    icon: <TreesIcon className="h-5 w-5" />,
  },
  {
    id: "light-v11",
    label: "Light",
    icon: <SunIcon className="h-5 w-5" />,
  },
  {
    id: "dark-v11",
    label: "Dark",
    icon: <MoonIcon className="h-5 w-5" />,
    isDark: true,
  },
];

interface MapStylesProps {
  /**
   * Called whenever the map style changes. Receives the style id and whether
   * the new style is a dark variant — useful for syncing a theme provider.
   */
  onStyleChange?: (styleId: string, isDark: boolean) => void;
}

export function MapStyles({ onStyleChange }: MapStylesProps) {
  const { map } = useMap();
  const [activeStyle, setActiveStyle] = useState("streets-v12");

  const handleChange = (value: string) => {
    if (!map) {
      return;
    }
    map.setStyle(`mapbox://styles/mapbox/${value}`);
    setActiveStyle(value);

    const option = STYLE_OPTIONS.find((s) => s.id === value);
    onStyleChange?.(value, option?.isDark ?? false);
  };

  return (
    <aside className="absolute bottom-4 left-4 z-10">
      <Tabs onValueChange={handleChange} value={activeStyle}>
        <TabsList className="bg-background shadow-lg">
          {STYLE_OPTIONS.map((style) => (
            <TabsTrigger
              className="flex items-center text-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground sm:px-3 sm:py-1.5"
              key={style.id}
              value={style.id}
            >
              {style.icon}
              <span className="hidden sm:inline">{style.label}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </aside>
  );
}
